import type { ThemeDoc } from '@event67/shared';
import type { ReactNode } from 'react';
import { ThemeStyle } from './ThemeStyle.js';

/**
 * The page frame every route renders into.
 *
 * `width` is the only knob: a form is a narrow column of controls, prose is a
 * reading measure, a schedule wants the full content width. All three sit on the
 * event's background, carry the theme, and start with a skip link — the form
 * pages in particular are reached from an email on a laptop, where tabbing is
 * the primary input.
 *
 * `form` is narrower than `reading` deliberately. A column of single-line inputs
 * set to a reading measure reads as a spreadsheet: the eye has to travel the
 * width of a paragraph to get from a label to the end of a one-word answer.
 */
export function PageShell({
  theme,
  children,
  width = 'content',
  header,
  footer,
}: {
  theme: ThemeDoc;
  children: ReactNode;
  width?: 'form' | 'reading' | 'content';
  header?: ReactNode;
  footer?: ReactNode;
}) {
  const measure =
    width === 'form' ? 'max-w-form' : width === 'reading' ? 'max-w-reading' : 'max-w-5xl';
  return (
    <div className="flex min-h-dvh flex-col bg-brand-bg">
      <ThemeStyle theme={theme} />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-brand focus:bg-brand-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-brand-on-primary"
      >
        Skip to content
      </a>
      {header}
      <main id="main" className={`mx-auto w-full flex-1 px-4 py-8 sm:px-6 sm:py-12 ${measure}`}>
        {children}
      </main>
      {footer}
    </div>
  );
}
